/**
 * Foundry's text enrichers, rendered for this app (docs/design.md, Source data; ADR-0007):
 * `@UUID[...]{label}` becomes a link that opens the Entry in the viewer, `@Check[...]`,
 * `@Damage[...]` and `[[/r ...]]` become Roll buttons carrying a RollRequest (src/ui/rolls.ts)
 * with the current Character's numbers, `@Template` and `[[/act]]` render as text and links, and
 * anything unknown renders as its label. The patterns are Foundry's (src/scripts/hooks/init.ts
 * and the core content-link and inline-roll enrichers); the labels follow
 * src/module/system/text-editor.ts. The output is HTML for `dangerouslySetInnerHTML`; the page
 * delegates clicks on `a.entry-link` and `button.roll` (see `readEnrichedClick`).
 */
import { levelBasedDC } from "../engine/dc";
import { FormulaError, evaluateFormula, replaceFormulaData } from "../engine/formula";
import { SAVES, SAVE_LABELS, SKILLS } from "../engine/values";
import type { IndexRecord } from "../../shared/snapshot-format";
import type { RollRequest } from "./rolls";

/** What the Character brings to a description: its Level, its statistics and its `@actor` roll data. */
export interface ActorContext {
  level: number;
  proficiencyWithoutLevel: boolean;
  /** A check statistic by Foundry's inline type (a skill or lore slug, a save, "perception", a special statistic), or null. */
  statistic(type: string): CheckStatistic | null;
  /** `@actor.*` and `@self.*` roll data; undefined when the path is unknown. */
  lookup(path: string): unknown;
}

export interface CheckStatistic {
  slug: string;
  label: string;
  modifier: number;
}

/** What the Entry the description belongs to brings: its Level, a spell's rank, `@item.*` roll data. */
export interface ItemContext {
  name: string;
  traits: string[];
  lookup(path: string): unknown;
}

export interface LinkTarget {
  resolve(uuid: string): IndexRecord | null;
  action(slug: string): IndexRecord | null;
}

export interface EnrichContext {
  links?: LinkTarget | null;
  actor?: ActorContext | null;
  item?: ItemContext | null;
}

/* ------------------------------------------------------------------------------------------ */
/* Scanning                                                                                   */
/* ------------------------------------------------------------------------------------------ */

type Token =
  | { kind: "text"; text: string }
  | { kind: "at"; name: string; params: string; label: string | null; raw: string }
  | { kind: "inline"; command: string | null; body: string; label: string | null; raw: string };

const AT_START = /@([A-Za-z]+)\[/y;
const INLINE_COMMAND = /^\/([a-z]+)(?:\s+|$)/i;

/** Split description HTML into text and enricher tokens; brackets nest one level (`@Damage[2d6[fire]]`). */
export function tokenize(html: string): Token[] {
  const tokens: Token[] = [];
  let text = "";
  let i = 0;
  const flush = () => {
    if (text) tokens.push({ kind: "text", text });
    text = "";
  };
  const readLabel = (from: number): [string | null, number] => {
    if (html[from] !== "{") return [null, from];
    const end = html.indexOf("}", from);
    if (end < 0) return [null, from];
    return [html.slice(from + 1, end), end + 1];
  };
  const bracketEnd = (from: number, depth: number): number => {
    for (let j = from; j < html.length; j++) {
      const ch = html[j];
      if (ch === "[") depth++;
      else if (ch === "]") {
        depth--;
        if (depth === 0) return j;
      }
    }
    return -1;
  };
  while (i < html.length) {
    const ch = html[i]!;
    if (ch === "@") {
      AT_START.lastIndex = i;
      const m = AT_START.exec(html);
      if (m) {
        const end = bracketEnd(i + m[0].length, 1);
        if (end > 0) {
          const params = html.slice(i + m[0].length, end);
          const [label, next] = readLabel(end + 1);
          flush();
          tokens.push({ kind: "at", name: m[1]!, params, label, raw: html.slice(i, next) });
          i = next;
          continue;
        }
      }
    } else if (ch === "[" && html[i + 1] === "[") {
      const end = bracketEnd(i + 2, 2);
      if (end > 0 && html[end - 1] === "]") {
        const inner = html.slice(i + 2, end - 1);
        const [label, next] = readLabel(end + 1);
        const cmd = INLINE_COMMAND.exec(inner);
        flush();
        tokens.push({ kind: "inline", command: cmd ? cmd[1]!.toLowerCase() : null, body: cmd ? inner.slice(cmd[0].length) : inner, label, raw: html.slice(i, next) });
        i = next;
        continue;
      }
    }
    text += ch;
    i++;
  }
  flush();
  return tokens;
}

/** Foundry's `#parseInlineParams`: `|`-separated `key:value` pairs, the first bare part taking the given key. */
export function parseParams(paramString: string, first: string): Record<string, string> {
  const out: Record<string, string> = {};
  paramString.split("|").forEach((part, idx) => {
    if (idx === 0 && !part.includes(":")) {
      out[first] = part.trim();
      return;
    }
    const colon = part.indexOf(":");
    if (colon < 0) out[part.trim()] = "";
    else out[part.slice(0, colon).trim()] = part.slice(colon + 1);
  });
  return out;
}

function splitList(value: string | undefined): string[] {
  return (value ?? "").split(",").map((s) => s.trim()).filter(Boolean);
}

/* ------------------------------------------------------------------------------------------ */
/* HTML helpers                                                                               */
/* ------------------------------------------------------------------------------------------ */

export function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function rollButton(request: RollRequest, inner: string, extraClass = ""): string {
  return `<button type="button" class="roll roll-${request.kind}${extraClass ? ` ${extraClass}` : ""}" data-roll="${escapeHtml(JSON.stringify(request))}">${inner}</button>`;
}

function entryLink(record: IndexRecord, label: string, extraClass = ""): string {
  const legacy = record.rm ? "" : ` <span class="tag legacy">Legacy</span>`;
  return `<a href="#" class="entry-link${extraClass ? ` ${extraClass}` : ""}" data-entry="${escapeHtml(record.u)}">${label}</a>${legacy}`;
}

function missingLink(label: string): string {
  return `<span class="entry-missing" title="Not available offline">${label}<small class="entry-missing-hint"> (not available offline)</small></span>`;
}

function titleCase(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.slice(0, 1).toUpperCase() + w.slice(1))
    .join(" ");
}

/* ------------------------------------------------------------------------------------------ */
/* Links                                                                                      */
/* ------------------------------------------------------------------------------------------ */

function renderUuid(uuid: string, label: string | null, ctx: EnrichContext, extraClass = ""): string {
  const record = ctx.links?.resolve(uuid.trim()) ?? null;
  if (record) return entryLink(record, label ?? escapeHtml(record.n), extraClass);
  return missingLink(label ?? escapeHtml(uuid.slice(uuid.lastIndexOf(".") + 1)));
}

/** `@Compendium[pf2e.feats-srd.abc]`: the pre-v10 form of a Compendium UUID. */
function renderCompendium(params: string, label: string | null, ctx: EnrichContext): string {
  const parts = params.trim().split(".");
  if (parts.length === 3) return renderUuid(`Compendium.${parts[0]}.${parts[1]}.Item.${parts[2]}`, label, ctx);
  return missingLink(label ?? escapeHtml(params));
}

/* ------------------------------------------------------------------------------------------ */
/* Checks                                                                                     */
/* ------------------------------------------------------------------------------------------ */

const SAVE_SET = new Set<string>(SAVES);

/** Foundry's simple check label: a save, a skill, Perception, a lore, else the slug in title case. */
export function checkLabel(type: string): string {
  if (SAVE_SET.has(type)) return SAVE_LABELS[type as keyof typeof SAVE_LABELS];
  if (type in SKILLS) return SKILLS[type as keyof typeof SKILLS].label;
  if (type === "perception") return "Perception";
  if (type === "flat") return "Flat check";
  if (type === "class") return "Class DC";
  return titleCase(type);
}

function resolveTerms(expression: string, ctx: EnrichContext): { formula: string; unresolved: string[] } {
  return replaceFormulaData(expression, (path) => {
    if (path.startsWith("actor.") || path.startsWith("self.")) return ctx.actor?.lookup(path);
    if (path.startsWith("item.")) return ctx.item?.lookup(path);
    return undefined;
  });
}

function evaluateNumber(expression: string, ctx: EnrichContext): number | null {
  const { formula, unresolved } = resolveTerms(expression, ctx);
  if (unresolved.length > 0) return null;
  try {
    const value = evaluateFormula(formula);
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  } catch (e) {
    if (e instanceof FormulaError) return null;
    throw e;
  }
}

/** The DC an inline check names: a number, `@self.level` (the level-based DC), or `resolve(...)` over roll data. */
export function resolveDC(raw: string | undefined, ctx: EnrichContext): number | null {
  const dc = raw?.trim();
  if (!dc) return null;
  if (/^-?\d+$/.test(dc)) return Number(dc);
  if (dc === "@self.level") return ctx.actor ? levelBasedDC(ctx.actor.level, { proficiencyWithoutLevel: ctx.actor.proficiencyWithoutLevel }) : null;
  const resolve = /^resolve\((.+)\)$/.exec(dc);
  return evaluateNumber(resolve ? resolve[1]! : dc, ctx);
}

const AGAINST_LABELS: Record<string, string> = { ac: "AC", fortitude: "Fortitude DC", reflex: "Reflex DC", will: "Will DC", perception: "Perception DC", "class-spell-dcs": "class or spell DC", class: "Class DC", spell: "spell DC" };

function renderCheck(params: string, inlineLabel: string | null, ctx: EnrichContext): string {
  const raw = parseParams(params, "type");
  const types = splitList(raw.type).map((t) => t.toLowerCase().replace(/\s+/g, "-"));
  if (types.length === 0) return escapeHtml(inlineLabel ?? params);
  const adjustments = splitList(raw.adjustment ?? "0");
  const basic = "basic" in raw;
  const against = (raw.against ?? raw.defense)?.trim() || null;
  const traits = splitList(raw.traits);
  const options = [...(basic ? ["damaging-effect"] : []), ...splitList(raw.options)].sort();
  const name = raw.name?.trim() || null;
  const dc = resolveDC(raw.dc, ctx);
  const buttons = types.map((type, i) => {
    const adjustment = Number(adjustments[i] ?? adjustments[0] ?? "0") || 0;
    const statistic = type === "flat" ? null : (ctx.actor?.statistic(type) ?? null);
    const baseLabel = inlineLabel ?? (SAVE_SET.has(type) && basic ? `basic ${SAVE_LABELS[type as keyof typeof SAVE_LABELS]}` : checkLabel(type));
    const shownDC = dc === null ? null : dc + adjustment;
    const label = shownDC !== null ? `DC ${shownDC} ${baseLabel}` : baseLabel;
    const modifier = type === "flat" ? 0 : statistic ? statistic.modifier : null;
    const request: RollRequest = {
      kind: "check",
      type,
      statistic: statistic?.slug ?? null,
      label,
      modifier,
      dc: shownDC,
      against: against && shownDC === null ? against : null,
      basic,
      traits,
      options,
      name,
    };
    const suffix = against && shownDC === null ? `<span class="roll-against">vs. ${escapeHtml(AGAINST_LABELS[against] ?? titleCase(against))}</span>` : "";
    const mod = modifier !== null && type !== "flat" ? `<span class="roll-mod">${signed(modifier)}</span>` : "";
    return rollButton(request, `<span class="roll-label">${escapeHtml(label)}</span>${mod}${suffix}`, statistic || type === "flat" ? "" : "roll-unknown");
  });
  return buttons.join(" or ");
}

function signed(n: number): string {
  return n < 0 ? String(n) : `+${n}`;
}

/* ------------------------------------------------------------------------------------------ */
/* Damage                                                                                     */
/* ------------------------------------------------------------------------------------------ */

const DAMAGE_CATEGORIES = new Set(["persistent", "precision", "splash"]);

/** Fold parenthesised arithmetic with no dice inside into numbers: `(3)d6` becomes `3d6`, `(floor(5/2))d4` `2d4`. */
function foldArithmetic(formula: string): string {
  let out = formula;
  for (let i = 0; i < 8; i++) {
    let changed = false;
    out = out.replace(/(\b[a-z]+)?\(([^()]*)\)/gi, (match, fn: string | undefined, inner: string) => {
      if (/\d\s*d\s*\d/i.test(inner) || /[a-z@]/i.test(inner)) return match;
      try {
        const value = evaluateFormula(fn ? `${fn}(${inner})` : inner);
        if (typeof value !== "number" || !Number.isFinite(value)) return match;
        changed = true;
        return String(value);
      } catch {
        return match;
      }
    });
    if (!changed) break;
  }
  return out;
}

/** Split `a[x],b[y]` at top-level commas (brackets and braces nest). */
function splitInstances(formula: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of formula) {
    if (ch === "[" || ch === "{" || ch === "(") depth++;
    if (ch === "]" || ch === "}" || ch === ")") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  if (cur.trim()) out.push(cur);
  return out.map((s) => s.trim()).filter(Boolean);
}

export interface DamageInstance {
  expression: string;
  types: string[];
  category: string | null;
}

/** Read a damage formula into its instances: `{2d6[fire],1d4[persistent,cold]}` gives two, the second persistent. */
export function parseDamage(formula: string): DamageInstance[] {
  const trimmed = formula.trim();
  const body = trimmed.startsWith("{") && trimmed.endsWith("}") ? trimmed.slice(1, -1) : trimmed;
  return splitInstances(body).map((instance) => {
    const m = /^(.*?)\[([^[\]]*)\]$/.exec(instance);
    if (!m) return { expression: instance.trim(), types: [], category: null };
    const flavour = splitList(m[2]);
    return { expression: m[1]!.trim(), types: flavour.filter((f) => !DAMAGE_CATEGORIES.has(f)), category: flavour.find((f) => DAMAGE_CATEGORIES.has(f)) ?? null };
  });
}

/** "2d6 fire", "1d4 persistent cold", "5" for untyped. */
export function damageLabel(instances: DamageInstance[], short = false): string {
  return instances
    .map((i) => {
      const expression = i.expression.replace(/^\((.*)\)$/, "$1");
      if (short) return expression;
      return [expression, i.category, ...i.types.filter((t) => t !== "untyped")].filter(Boolean).join(" ");
    })
    .join(short ? " + " : ", ");
}

function renderDamage(params: string, inlineLabel: string | null, ctx: EnrichContext): string {
  const raw = parseParams(params, "formula");
  const formula = raw.formula?.trim();
  if (!formula) return escapeHtml(inlineLabel ?? params);
  const resolved = resolveTerms(formula, ctx);
  const folded = foldArithmetic(resolved.formula);
  const instances = parseDamage(folded);
  const full = damageLabel(instances);
  const label = inlineLabel ?? ("shortLabel" in raw ? damageLabel(instances, true) : full);
  const traits = [...new Set([...splitList(raw.traits), ...("overrideTraits" in raw ? [] : (ctx.item?.traits ?? []))])];
  const request: RollRequest = { kind: "damage", formula: full, raw: folded, label, traits, options: splitList(raw.options), name: raw.name?.trim() || null, unresolved: resolved.unresolved };
  return rollButton(request, `<span class="roll-label">${escapeHtml(label)}</span>`, resolved.unresolved.length > 0 ? "roll-unknown" : "");
}

/* ------------------------------------------------------------------------------------------ */
/* Inline rolls, actions, templates                                                           */
/* ------------------------------------------------------------------------------------------ */

const ROLL_MODES: Record<string, "r" | "gmr" | "br" | "sr"> = { r: "r", roll: "r", pr: "r", publicroll: "r", gmr: "gmr", gmroll: "gmr", br: "br", blindroll: "br", sr: "sr", selfroll: "sr" };

function renderInlineRoll(command: string | null, body: string, label: string | null): string {
  const mode = command === null ? "r" : ROLL_MODES[command];
  if (mode === undefined) return escapeHtml(label ?? body);
  const hash = body.indexOf("#");
  const formula = (hash >= 0 ? body.slice(0, hash) : body).trim();
  const flavor = hash >= 0 ? body.slice(hash + 1).trim() || null : null;
  if (!formula) return escapeHtml(label ?? body);
  const shown = label ?? formula;
  const request: RollRequest = { kind: "inline", formula, label: shown, flavor, mode };
  const title = flavor ? ` title="${escapeHtml(flavor)}"` : "";
  return `<button type="button" class="roll roll-inline" data-roll="${escapeHtml(JSON.stringify(request))}"${title}><span class="roll-label">${escapeHtml(shown)}</span></button>`;
}

/** `[[/act seek dc=20 statistic=perception]]{label}`: the action Entry as a link, with the DC and statistic in words. */
function renderAction(body: string, label: string | null, ctx: EnrichContext): string {
  const m = /^([-a-z]+)(?:\s+(.*))?$/s.exec(body.trim());
  if (!m) return escapeHtml(label ?? body);
  const slug = m[1]!;
  const options: Record<string, string> = {};
  for (const option of (m[2] ?? "").split(/\s+/).filter(Boolean)) {
    const [key, value] = option.split("=");
    if (key) options[key.trim()] = (value ?? "").trim();
  }
  const record = ctx.links?.action(slug) ?? null;
  const text = label?.trim() ? escapeHtml(label.trim()) : record ? escapeHtml(record.n) : escapeHtml(titleCase(slug));
  const head = record ? entryLink(record, text, "action-link") : `<span class="action-text">${text}</span>`;
  const dc = options["difficulty-class"] ?? options.dc;
  const statistic = options.statistic ?? options.stat ?? options.skill;
  const details = dc && statistic ? `DC ${escapeHtml(dc)} ${escapeHtml(checkLabel(statistic))}` : dc ? `DC ${escapeHtml(dc)}` : statistic ? escapeHtml(checkLabel(statistic)) : "";
  return details ? `${head} <span class="action-details">(${details})</span>` : head;
}

function renderTemplate(params: string, label: string | null): string {
  const raw = parseParams(params, "type");
  const type = raw.type?.trim() ?? "";
  const distance = raw.distance?.trim() ?? "";
  const text = label ?? (type && distance ? `${distance}-foot ${type}` : params);
  return `<span class="template">${escapeHtml(text)}</span>`;
}

/* ------------------------------------------------------------------------------------------ */
/* Entry point                                                                                */
/* ------------------------------------------------------------------------------------------ */

const DOCUMENT_TYPES = new Set(["Item", "Actor", "JournalEntry", "RollTable", "Macro", "Scene", "Playlist", "Cards"]);

/** Render one enricher token. Exported for tests; `enrichDescription` renders a whole description. */
export function renderToken(token: Token, ctx: EnrichContext): string {
  if (token.kind === "text") return token.text;
  if (token.kind === "inline") {
    if (token.command === "act") return renderAction(token.body, token.label, ctx);
    return renderInlineRoll(token.command, token.body, token.label);
  }
  switch (token.name) {
    case "UUID": return renderUuid(token.params, token.label, ctx);
    case "Embed": return renderUuid(token.params.split(/\s+/)[0] ?? "", token.label, ctx, "embed-link");
    case "Compendium": return renderCompendium(token.params, token.label, ctx);
    case "Check": return renderCheck(token.params, token.label, ctx);
    case "Damage": return renderDamage(token.params, token.label, ctx);
    case "Template": return renderTemplate(token.params, token.label);
    case "Localize": return `<span class="muted">${escapeHtml(token.label ?? token.params)}</span>`;
    default:
      if (DOCUMENT_TYPES.has(token.name)) return missingLink(token.label ?? escapeHtml(token.params));
      return escapeHtml(token.label ?? token.params);
  }
}

/** Description HTML with every enricher rendered. */
export function enrichDescription(html: string, ctx: EnrichContext = {}): string {
  return tokenize(html).map((t) => renderToken(t, ctx)).join("");
}

/** What a tap inside enriched HTML means: an Entry to open, a Roll to make, or nothing. */
export function readEnrichedClick(target: EventTarget | null): { kind: "entry"; uuid: string } | { kind: "roll"; request: RollRequest } | null {
  if (!(target instanceof Element)) return null;
  const link = target.closest("a.entry-link");
  if (link) {
    const uuid = link.getAttribute("data-entry");
    return uuid ? { kind: "entry", uuid } : null;
  }
  const button = target.closest("button.roll");
  const data = button?.getAttribute("data-roll");
  if (data) {
    try {
      return { kind: "roll", request: JSON.parse(data) as RollRequest };
    } catch {
      return null;
    }
  }
  return null;
}
